import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from "react";
import { pausableInterval } from "@/lib/appVisibility";
import { PostListContext } from "@/lib/postList";
import { Link, useNavigate } from "react-router-dom";
import { consumeAfterAuth } from "@/lib/afterAuth";
import { AppShell } from "@/components/AppShell";
import { StoryBar } from "@/components/StoryBar";
import { PostComposer } from "@/components/PostComposer";
import { BioNudgeCard } from "@/components/BioNudgeCard";
import { shouldNudgeBio, bioNudgePending } from "@/lib/bioNudge";
import { AvatarNudgeCard } from "@/components/AvatarNudgeCard";
import { NameNudgeCard, nameNudgeDismissed } from "@/components/NameNudgeCard";
import { AppLinksNudgeCard } from "@/components/AppLinksNudgeCard";
import { shouldNudgeAvatar } from "@/lib/avatarNudge";
import { BackupEmailNudgeCard } from "@/components/BackupEmailNudgeCard";
import { PendingRecoveryCard } from "@/components/PendingRecoveryCard";
import { shouldNudgeBackupEmail } from "@/lib/backupEmailNudge";
import { onPostPatch, patchPostList, usePostListSync } from "@/lib/postSync";
import { VerifyAccountReminder } from "@/components/VerifyAccountReminder";
import { PostCard } from "@/components/PostCard";
import { FeedWindow } from "@/components/FeedWindow";
import { pickTopNudgePostId } from "@/lib/promoteNudge";
import { adEvery as adEveryFor } from "@/lib/adFreq";
import { feedGoogleEnabled, feedGoogleEvery, reelsAdMode } from "@/lib/admob";
import { FollowSuggestions } from "@/components/FollowSuggestions";
import { BirthdayWidget } from "@/components/BirthdayWidget";
import { FeaturedTestimony } from "@/components/FeaturedTestimony";
import { LiveNowWidget } from "@/components/LiveNowWidget";
import { PrayersSpotlightWidget } from "@/components/PrayersSpotlightWidget";
import { PrayerChainBanner } from "@/components/PrayerChainBanner";
import { useSmartFeedCards } from "@/components/SmartFeedCards";
import { ProfileCompletionCard } from "@/components/ProfileCompletionCard";
import { ScheduledLives } from "@/components/ScheduledLives";
import { FeedSuggestions } from "@/components/FeedSuggestions";
import { FeedSkeleton } from "@/components/FeedSkeleton";
import { FeedEnd } from "@/components/FeedEnd"; // lot 683 — pied de liste : chargement / Réessayer / « Tu as tout vu »
import { GoogleAd } from "@/components/GoogleAd";
import { api } from "@/lib/api";
import { isOffline } from "@/lib/netStatus";
import { readCachedFeed, writeCachedFeed } from "@/lib/offlineCache";
import { seedFeedFromCache } from "@/lib/instantBoot";
import { prefetchedGet } from "@/lib/bootPrefetch";
import { markFeedReady } from "@/lib/lowPriority";
import { preloadHls } from "@/lib/videoSource";
import { Capacitor } from "@capacitor/core";
import { useApp } from "@/context/AppContext";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useRefreshOnDemand, scrollToTop } from "@/lib/topRefresh";
import { collapseGroupShares, mergeFeedPages, refreshInPlace, knownIdSet } from "@/lib/feedShares";
import { useGuard } from "@/lib/guards";
import { useUserBlocked, withoutAuthor } from "@/lib/blocks";
import { captureScrollAnchor, useScrollAnchorRestore, startAnchorRestore } from "@/lib/scrollAnchor";
import { isPositionFresh, onPositionMemoryExpire } from "@/lib/positionMemory";
import { saveFeedResume, clearFeedResume, takeFeedResume, postIdFromTestId, RESUME_MIN_SCROLL_PX, RESUME_MAX_PAGES } from "@/lib/feedResume";
import { lastBootUnexpected } from "@/lib/flightRecorder";
import { Heart, Church, RefreshCw, ArrowUp, Globe, Compass } from "lucide-react";
import { FeedModeNudgeCard } from "@/components/FeedModeNudgeCard";

// Ordre par défaut des blocs du fil (identique au seed) : utilisé si /config/public n'a jamais répondu.
const DEFAULT_MAIN_BLOCKS = ["home_cta", "stories", "create_post", "feed_filters", "live_now", "feed_posts"]
  .map((key, i) => ({ key, zone: "main_feed", order: i + 1, visible: true, enabled: true }));

// Module-level snapshot: coming BACK to the feed renders the last posts
// instantly (Facebook-style) while load() silently refreshes in the background.
let feedSnapshot = null;
// Délai de mémorisation (admin) dépassé → le fil repart de zéro, en haut.
onPositionMemoryExpire(() => { feedSnapshot = null; });
// Reactions / comments made elsewhere (viewer, theater, profile) land in the snapshot too,
// so coming back to the feed shows them instantly — before the silent refresh.
onPostPatch((id, patch) => { if (feedSnapshot) feedSnapshot = { ...feedSnapshot, posts: patchPostList(feedSnapshot.posts, id, patch) }; });

export const Home = () => {
  const { user, config, t, reconnecting } = useApp();
  const navAfterAuth = useNavigate();
  useEffect(() => { const p = consumeAfterAuth(); if (p) navAfterAuth(p, { replace: true }); }, [navAfterAuth]);   // lot 771
  const isModerator = ["super_admin", "moderator"].includes(user?.role);
  const PAGE = 15;
  // Hors ligne / serveur injoignable au démarrage : le dernier fil lu (mémorisé sur l'appareil) s'affiche tout de suite.
  // Lot 716 — démarrage instantané (garde-fou instant_boot) : même amorce à chaque montage sans instantané, en ligne
  // aussi — le fil de la dernière visite apparaît immédiatement et load() le remplace en silence (pas de squelette).
  if (!feedSnapshot && user?.id) {
    feedSnapshot = seedFeedFromCache({ uid: user.id, cached: readCachedFeed(user.id), offline: !!(reconnecting || isOffline()) });
  }
  if (feedSnapshot && !isPositionFresh(feedSnapshot.at)) feedSnapshot = null;
  const restored = !!(feedSnapshot && feedSnapshot.uid === user?.id);
  const bootSeeded = restored && !!feedSnapshot.bootSeed;
  const [posts, setPosts] = useState(() => (restored ? feedSnapshot.posts : []));
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(() => !restored);
  const [loadError, setLoadError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false); // lot 683 — la page suivante a échoué (FeedEnd : Réessayer)
  const [hasMore, setHasMore] = useState(() => (restored ? feedSnapshot.hasMore : true));
  // Le filtre (Tous / Prédicateurs…) et « Ma langue » reviennent aussi du snapshot : on retrouve LE MÊME fil.
  const [filter, setFilter] = useState(() => (restored && feedSnapshot.filter) || "all");
  const [myLangOnly, setMyLangOnly] = useState(() => (restored && typeof feedSnapshot.myLangOnly === "boolean" ? feedSnapshot.myLangOnly : true));
  const myLang = (user?.language || "fr").toLowerCase().slice(0, 2);
  const mlDiscoveryOn = (config?.settings?.features?.multilingual_discovery) !== false;
  const showLangUi = (config?.settings?.features?.language_filter_ui) !== false;
  const [newAvailable, setNewAvailable] = useState(false);
  const postsRef = useRef([]);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const lastQueryRef = useRef(restored ? feedSnapshot.key || null : null);
  // Lot 609 — mode d'affichage du fil (réglage système) : périmètre renvoyé par /posts + curseur 2 phases (« Abonnements d'abord »).
  const [feedScope, setFeedScope] = useState(() => (restored && feedSnapshot.feedScope) || null);
  const groupSharesOn = useGuard("feed_group_shares"); // lot 746
  const cursorRef = useRef(restored ? feedSnapshot.cursor || null : null);
  useEffect(() => { postsRef.current = posts; }, [posts]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  usePostListSync(setPosts);

  const load = useCallback(async ({ force = false } = {}) => {
    const cur = postsRef.current;
    const silent = cur.length > 0; // posts already on screen → refresh without skeleton
    if (!silent) setLoading(true);
    const key = `${filter}|${myLangOnly}|${myLang}`;
    const sameQuery = lastQueryRef.current === key;
    lastQueryRef.current = key;
    try {
      const { data } = await prefetchedGet("/posts", { params: { limit: PAGE, ...(filter !== "all" ? { filter } : {}), ...((mlDiscoveryOn && myLangOnly) ? { lang: myLang } : {}) } });
      const list = data.posts || [];
      if (data.feed_scope) setFeedScope(data.feed_scope);
      cursorRef.current = data.feed_cursor || null;
      markFeedReady(); // 1re page reçue → les widgets secondaires peuvent lancer leurs requêtes
      // Préchauffer le chunk hls.js (hors iPhone : lecteur natif) pour que la 1re vidéo n'attende pas son téléchargement.
      if (Capacitor.getPlatform() !== "ios") preloadHls();
      // Back on a feed scrolled deep (multi-page list restored from the snapshot): never
      // truncate it to page 1 under the member's thumb — refresh the loaded posts in place
      // and surface the « new posts » pill instead (same rule as the 35 s poll below).
      if (silent && sameQuery && !force && window.scrollY >= 200 && cur.length > list.length) {
        const merged = refreshInPlace(cur, list);
        setPosts(merged);
        const known = knownIdSet(cur); // lot 746 — un partage déjà fusionné n'est pas « nouveau »
        if (list.some((p) => !known.has(p.id))) setNewAvailable(true);
        feedSnapshot = { uid: user?.id, posts: merged, hasMore: hasMoreRef.current, key, filter, myLangOnly, feedScope: data.feed_scope, cursor: feedSnapshot?.cursor, at: Date.now() };
      } else {
        const shown = collapseGroupShares(list, groupSharesOn); // lot 746 — une seule carte par publication partagée dans plusieurs groupes
        setPosts(shown);
        setHasMore(list.length >= PAGE);
        setNewAvailable(false);
        feedSnapshot = { uid: user?.id, posts: shown, hasMore: list.length >= PAGE, key, filter, myLangOnly, feedScope: data.feed_scope, cursor: cursorRef.current, at: Date.now() };
      }
      if (list.length && user?.id) writeCachedFeed(user.id, list, { key, filter, myLangOnly }); // dernier fil lu (mode hors ligne)
      setLoadError(false);
    } catch {
      setLoadError(true); // réseau coupé / lent : carte « Réessayer » plutôt qu'un fil vide…
      if (!postsRef.current.length && user?.id) { // …ou le dernier fil lu s'il existe
        const c = readCachedFeed(user.id);
        if (c) { const shown = collapseGroupShares(c.posts, groupSharesOn); setPosts(shown); setHasMore(false); feedSnapshot = { uid: user.id, posts: shown, hasMore: false, key, filter, myLangOnly, fromCache: true, at: Date.now() }; }
      }
    }
    setLoading(false);
  }, [filter, myLangOnly, myLang, groupSharesOn]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(false);
    try {
      const cur = postsRef.current;
      const cursor = cursorRef.current;
      const before = cursor?.before || (cur.length ? cur[cur.length - 1].created_at : undefined);
      const phase = cursor?.phase === "rest" ? "rest" : undefined;
      const { data } = await api.get("/posts", { params: { limit: PAGE, ...(before ? { before } : {}), ...(phase ? { phase } : {}), ...(filter !== "all" ? { filter } : {}), ...((mlDiscoveryOn && myLangOnly) ? { lang: myLang } : {}) } });
      const list = data.posts || [];
      cursorRef.current = data.feed_cursor || null;
      setPosts((prev) => mergeFeedPages(prev, list, groupSharesOn)); // lot 746 — fusion des partages en groupe entre pages
      setHasMore(list.length >= PAGE);
    } catch {
      // Lot 683 — plus d'échec muet : FeedEnd affiche « Réessayer » et retente seul au retour du réseau.
      setLoadMoreError(true);
    }
    setLoadingMore(false);
    loadingMoreRef.current = false;
  }, [filter, myLangOnly, myLang, groupSharesOn]);

  const refreshAll = useCallback(async () => {
    await load({ force: true });
    window.dispatchEvent(new Event("yalola:story_created")); // nudge stories/scheduled to reload
  }, [load]);
  const { pull, refreshing } = usePullToRefresh(refreshAll, { threshold: 70 });

  useEffect(() => { load(); }, [load]);
  // Retour du réseau / serveur → le fil se recharge tout seul s'il avait échoué (ou venait du cache).
  useEffect(() => {
    if (!loadError && !feedSnapshot?.fromCache) return undefined;
    const again = () => load({ force: true });
    ["online", "yalola:network-online", "yalola:reconnected"].forEach((e) => window.addEventListener(e, again));
    return () => ["online", "yalola:network-online", "yalola:reconnected"].forEach((e) => window.removeEventListener(e, again));
  }, [loadError, load]);

  // Leaving the feed (post, viewer, Reels, profile, …): snapshot the WHOLE loaded list, the filter
  // and the post anchored at the top of the viewport, so coming back restores every page, the same
  // filter and the exact scroll position. Layout effect: the cards are still in the DOM at that point.
  useLayoutEffect(() => () => {
    if (postsRef.current.length) {
      feedSnapshot = { uid: user?.id, posts: postsRef.current, hasMore: hasMoreRef.current, key: `${filter}|${myLangOnly}|${myLang}`, filter, myLangOnly, feedScope, cursor: cursorRef.current, anchor: captureScrollAnchor("article.yalola-card[data-testid^='post-']"), at: Date.now() };
    }
  }, [filter, myLangOnly, myLang, user?.id]);

  // Anchor-based scroll restore (lib/scrollAnchor): widgets above the feed (stories, lives, ads…)
  // load async and shift the layout, so the browser's own restore lands hundreds of px off.
  const anchorRef = useRef(restored ? feedSnapshot.anchor : null);
  useScrollAnchorRestore(anchorRef.current);

  // Lot 682 — reprise du fil après un rechargement involontaire (iOS : processus web tué → page rechargée).
  // 1) pendant la lecture : la publication en haut de l'écran est notée sur l'appareil (≤ 1 écriture / 1,5 s) ;
  // 2) au démarrage : si la relance est classée involontaire et la trace récente, on rouvre sur cette publication,
  //    en chargeant jusqu'à RESUME_MAX_PAGES pages de plus si elle n'est pas dans la première.
  // `undefined` = pas encore décidé (la session peut n'être connue qu'au 2e rendu) ; null = pas de reprise.
  const resumeRef = useRef(undefined);
  if (resumeRef.current === undefined && user?.id) resumeRef.current = restored && !bootSeeded ? null : takeFeedResume(user.id, { unexpected: lastBootUnexpected() });
  const resumePagesRef = useRef(0);
  useEffect(() => {
    if (!user?.id) return undefined;
    let timer = null;
    const note = () => {
      timer = null;
      if (window.scrollY < RESUME_MIN_SCROLL_PX) { clearFeedResume(); return; }
      const a = captureScrollAnchor("article.yalola-card[data-testid^='post-']");
      const postId = a ? postIdFromTestId(a.sel.replace(/^\[data-testid="|"\]$/g, "")) : null;
      if (postId) saveFeedResume({ uid: user.id, postId, top: a.top, filter });
    };
    const onScroll = () => { if (!timer) timer = setTimeout(note, 1500); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (timer) clearTimeout(timer); };
  }, [user?.id, filter]);
  const resumeCancelRef = useRef(null);
  useEffect(() => {
    const r = resumeRef.current;
    // Lot 716 — fil amorcé depuis le cache : attendre la liste fraîche (load() la remplace) avant de ré-ancrer.
    if (!r || loading || !posts.length || feedSnapshot?.fromCache) return;
    if (posts.some((p) => p.id === r.postId)) {
      resumeRef.current = null;
      // Le ré-ancrage doit survivre aux re-rendus (pages suivantes, images qui chargent) : annulé seulement au démontage.
      resumeCancelRef.current = startAnchorRestore({ sel: `[data-testid="post-${r.postId}"]`, index: 0, top: r.top });
      return;
    }
    if (hasMore && !loadingMore && resumePagesRef.current < RESUME_MAX_PAGES) { resumePagesRef.current += 1; loadMore(); }
    else if (!hasMore || resumePagesRef.current >= RESUME_MAX_PAGES) resumeRef.current = null; // introuvable : fil normal
  }, [posts, loading, hasMore, loadingMore, loadMore]);
  useEffect(() => () => { resumeCancelRef.current?.(); }, []);

  // Warm up the FIRST reel (list + CDN connection + manifest + first segment +
  // poster) while the member is still on Home, so tapping "Reels" starts
  // TikTok-instantly. Idle-delayed and best-effort — never disturbs the feed.
  useEffect(() => {
    const t = setTimeout(() => {
      import("@/lib/reelsWarmup").then((m) => m.warmFirstReel()).catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  // (lot 683 : la sentinelle et ses états — chargement / échec + Réessayer / « Tu as tout vu » — sont dans FeedEnd.)

  useEffect(() => {
    const onCreated = () => load({ force: true });
    window.addEventListener("yalola:post_created", onCreated);
    return () => window.removeEventListener("yalola:post_created", onCreated);
  }, [load]);
  // Délai de mémorisation dépassé alors que le fil est resté ouvert (app en arrière-plan) : haut du fil, rafraîchi.
  useEffect(() => {
    const onExpired = () => { window.scrollTo(0, 0); load({ force: true }); };
    window.addEventListener("yalola:position-expired", onExpired);
    return () => window.removeEventListener("yalola:position-expired", onExpired);
  }, [load]);
  // « Présente-toi » card: once, right after a publication, when the member has no intro yet.
  const [bioNudge, setBioNudge] = useState(false);
  useEffect(() => {
    // Lot 589 : la carte disparaît aussi dès que la bio existe (ex. saisie depuis « Complète ton profil »).
    const check = () => { if (bioNudgePending() && shouldNudgeBio(user)) setBioNudge(true); else if (!shouldNudgeBio(user)) setBioNudge(false); };
    check();
    window.addEventListener("yalola:bio_nudge", check);
    return () => window.removeEventListener("yalola:bio_nudge", check);
  }, [user]);
  // « Ajoute une photo de profil » card: once, as soon as a member on the default avatar opens Home
  // (the Bio card always takes precedence — never both at the same time).
  // Lot 589 : reflète l'état courant → disparaît dès qu'une photo est ajoutée ailleurs (feuille « Complète ton profil »).
  const [avatarNudge, setAvatarNudge] = useState(false);
  useEffect(() => { setAvatarNudge(shouldNudgeAvatar(user)); }, [user]);
  const [nameNudge, setNameNudge] = useState(false); // lot 532 : nom fantaisiste (users.name_issue)
  useEffect(() => { setNameNudge(!!user?.name_issue && !nameNudgeDismissed()); }, [user]);
  const [backupEmailNudge, setBackupEmailNudge] = useState(false); // lot 560 : compte WhatsApp sans e-mail (7 j après « Plus tard »)
  useEffect(() => { setBackupEmailNudge(shouldNudgeBackupEmail(user)); }, [user]);
  useUserBlocked(useCallback((uid) => setPosts((arr) => withoutAuthor(arr, uid)), []));
  useEffect(() => {
    api.get("/ads/feed").then(({ data }) => setAds(data.ads || [])).catch(() => {});
  }, []);
  // Lot 553 : cartes intelligentes (1 en tête, les autres après la 3e, 7e, 11e publication) ; désactivé → empilées.
  const smart = useSmartFeedCards();

  // Facebook-style auto-refresh: poll for newer posts and surface a "new posts" pill.
  useEffect(() => {
    const check = async () => {
      if (document.visibilityState !== "visible") return;
      const cur = postsRef.current;
      if (!cur.length) return;
      const topReal = cur.find((p) => p.created_at && !p.is_ad);
      if (!topReal) return;
      try {
        const { data } = await api.get("/posts", { params: { limit: 5, ...(filter !== "all" ? { filter } : {}), ...((mlDiscoveryOn && myLangOnly) ? { lang: myLang } : {}) } });
        const list = data.posts || [];
        const hasNew = list.some((p) => !cur.some((c) => c.id === p.id) && new Date(p.created_at) > new Date(topReal.created_at));
        if (hasNew) {
          // At the top of the feed → refresh silently (auto-reload). Scrolled
          // down → surface the pill so content doesn't jump under the thumb.
          if (window.scrollY < 200) load();
          else setNewAvailable(true);
        }
      } catch { /* ignore */ }
    };
    const stop = pausableInterval(check, 35000, { onResume: false }); // lot 699 — onVis ci-dessous fait déjà le rappel
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [filter, myLangOnly, myLang]);

  const showNewPosts = useCallback(() => {
    setNewAvailable(false);
    scrollToTop();
    load({ force: true });
  }, [load]);

  // Lot 661 : Retour Android (fil descendu) → remonte ; recharge seulement s'il y a du nouveau. Onglet / logo déjà en haut → actualise.
  // Lot 687 : l'événement est annulable — `preventDefault()` dit à NativeBackButton « pris en charge » (sinon il remonte lui-même).
  useEffect(() => {
    const onBackTop = (e) => { e?.preventDefault?.(); if (newAvailable) showNewPosts(); else scrollToTop(); };
    window.addEventListener("yalola:back-to-top", onBackTop);
    return () => window.removeEventListener("yalola:back-to-top", onBackTop);
  }, [newAvailable, showNewPosts]);
  useRefreshOnDemand(() => { setNewAvailable(false); refreshAll(); });

  // repostId fourni (republication annulée) → seule cette entrée du fil disparaît, l'original reste.
  const removePost = (id, repostId) => setPosts((arr) => arr.filter((p) => (repostId ? p.repost_id !== repostId : p.id !== id)));
  // Feed order + paging for the PC theater's ‹ › (next / previous video of the feed).
  const postListCtx = useMemo(() => ({ posts, loadMore, hasMore, loadingMore }), [posts, loadMore, hasMore, loadingMore]);

  // Config jamais reçue (1er lancement hors ligne) : blocs par défaut → jamais de page vide.
  const mainBlocks = (config?.layout_blocks?.length ? config.layout_blocks : DEFAULT_MAIN_BLOCKS)
    .filter(b => b.zone === "main_feed" && b.visible && b.enabled)
    .sort((a, b) => a.order - b.order);

  // Facebook-style feed assembly: inject a sponsored ad every 4 posts and a
  // "Suggestions à suivre" card after the 2nd post.
  const renderFeed = () => {
    if (loading) return <FeedSkeleton count={3} />;
    if (posts.length === 0 && loadError) return (
      <div className="yalola-card p-8 text-center space-y-3" data-testid="feed-error">
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          {t("feed.load_error", "Impossible de charger le fil. Vérifie ta connexion internet.")}
        </p>
        <button type="button" onClick={() => load({ force: true })} className="pill pill-primary inline-flex items-center gap-1.5" data-testid="feed-error-retry">
          <RefreshCw size={14} /> {t("feed.retry", "Réessayer")}
        </button>
      </div>
    );
    if (posts.length === 0) return (
      <div className="yalola-card p-8 text-center" data-testid="feed-empty" data-scope={feedScope?.active ? feedScope.mode : "everyone"}>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          {feedScope?.active
            ? t("feed.empty_following", "Aucune publication de tes abonnements pour le moment. Suis d'autres membres pour enrichir ton fil !")
            : t("feed.empty", "Aucune publication pour le moment. Sois le premier à partager quelque chose de béni !")}
        </p>
        {feedScope?.active && <FollowSuggestions variant="feed" />}
      </div>
    );
    const nodes = [];
    const feedAlgo = config?.settings?.algorithms?.feed || {};
    const adEvery = adEveryFor("ad_frequency", config, user, 4);
    // App native : fréquence AdMob « Fil » (Admin › Paramètres › Finance › Google AdMob) ; web : fréquence AdSense.
    const googleEvery = feedGoogleEvery(config?.settings) || Math.max(3, Number(config?.settings?.adsense?.feed_frequency) || 6);
    // App native, mode « En complément » (réglage admin) : quand une campagne Yalola tourne, les emplacements
    // publicitaires ALTERNENT Yalola → Google → Yalola… (comme le Profil). Sinon Google ne sert que de repli.
    const googleAlternates = feedGoogleEnabled(config?.settings) && reelsAdMode(config?.settings) === "complement";
    const suggPos = Math.max(1, Number(feedAlgo.suggestions_position) || 2);
    // Smart promote nudge: pick the single best-performing recent owned post (feed only).
    const topNudgeId = pickTopNudgePostId(posts, config?.settings?.promote_nudge, user?.id);
    let adIdx = 0;
    let slotNo = 0;
    let lastAdPos = -10;
    // Facebook-style discovery modules interspersed in the feed.
    const suggSlots = user ? [
      { at: 1, node: <PrayersSpotlightWidget key="fs-prayers" variant="feed" /> },
      { at: 2, node: <FeedSuggestions key="fs-groups" type="groups" /> },
      { at: 3, node: <FeedSuggestions key="fs-churches" type="churches" /> },
      { at: 5, node: <FeedSuggestions key="fs-events" type="events" /> },
      { at: 6, node: <FeedSuggestions key="fs-reels" type="reels" /> },
      { at: 8, node: <FeedSuggestions key="fs-market" type="marketplace" /> },
    ] : [];
    const injected = new Set();
    posts.forEach((p, i) => {
      // Lot 609 — « Abonnements d'abord » : séparateur avant le premier contenu hors abonnements.
      if (p.feed_section === "rest" && (i === 0 || posts[i - 1].feed_section !== "rest")) {
        nodes.push(
          <div key="feed-rest-divider" className="flex items-center gap-3 py-1" data-testid="feed-rest-divider">
            <div className="h-px flex-1" style={{ background: "var(--yalola-border)" }} />
            <span className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><Compass size={13} /> {t("feed.rest_divider", "Découvrir — membres que tu ne suis pas encore")}</span>
            <div className="h-px flex-1" style={{ background: "var(--yalola-border)" }} />
          </div>
        );
      }
      nodes.push(<FeedWindow key={p.repost_id || p.id} index={i} testId={`feed-window-${p.repost_id || p.id}`}><PostCard post={p} canModerate={isModerator} allowNudge={p.id === topNudgeId} onReactionChange={load} onDeleted={removePost} onHidden={removePost} /></FeedWindow>);
      const smartHere = smart.slotAt(i + 1);
      if (smartHere) nodes.push(...smartHere);
      if (user && i === suggPos - 1) {
        nodes.push(<FollowSuggestions key="sugg-feed" variant="feed" />);
      }
      suggSlots.forEach((s, idx) => { if (i === s.at) { nodes.push(s.node); injected.add(idx); } });
      // Ad arbiter — Yalola Direct campaigns come FIRST; Google (AdSense, web only)
      // is a fallback filler. One ad max per position, never two ads back-to-back
      // (at least 2 organic posts between two ads). App native « En complément » : les
      // emplacements Yalola alternent avec une carte Google (1er, 3e… Yalola ; 2e, 4e… Google).
      const pos = i + 1;
      const yalolaDue = pos % adEvery === 0 && ads.length > 0;
      const googleDue = pos % googleEvery === 0;
      if (yalolaDue) {
        slotNo += 1;
        if (googleAlternates && slotNo % 2 === 0) {
          nodes.push(<GoogleAd key={`gad-${i}`} slotKey="feed_slot" routeKey={`feed-${i}`} />);
        } else {
          const ad = ads[adIdx % ads.length];
          adIdx += 1;
          nodes.push(<FeedWindow key={`ad-${ad.id}-${i}`} index={i} testId={`feed-window-ad-${ad.id}-${i}`}><PostCard post={ad} onReactionChange={load} /></FeedWindow>);
        }
        lastAdPos = pos;
      } else if (googleDue && pos - lastAdPos >= 2) {
        nodes.push(<GoogleAd key={`gad-${i}`} slotKey="feed_slot" routeKey={`feed-${i}`} />);
        lastAdPos = pos;
      }
    });
    // Ensure every discovery module appears even on short feeds.
    suggSlots.forEach((s, idx) => { if (!injected.has(idx)) nodes.push(s.node); });
    const smartTail = smart.tail(posts.length);
    if (smartTail) nodes.push(...smartTail);
    nodes.push(<FeedEnd key="feed-end" hasMore={hasMore} loadingMore={loadingMore} error={loadMoreError} onLoadMore={loadMore} itemCount={posts.length} testId="feed-end" />);
    return <div className="space-y-4">{nodes}</div>;
  };

  const renderBlock = (b) => {
    switch (b.key) {
      case "home_cta":
        return (
          <div key="cta" className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="home-cta-block">
            <Link to="/matching" className="cta-hero cta-hero-coral" data-testid="cta-matching">
              <Heart size={20} fill="white" /> {t("matching.title", "Points en commun")}
            </Link>
            <Link to="/churches" className="cta-hero cta-hero-navy" data-testid="cta-churches">
              <Church size={20} /> {t("churches.title", "Trouve une église")}
            </Link>
          </div>
        );
      case "live_now":
        return (
          <ScheduledLives key="live-now" />
        );
      case "stories": return config?.settings?.features?.stories === false ? null : <StoryBar key="stories" />;
      case "create_post": return (
        <React.Fragment key="composer">
          <PostComposer onPublished={(p) => setPosts([p, ...posts])} />
          {nameNudge
            ? <NameNudgeCard onClose={() => setNameNudge(false)} />
            : bioNudge
              ? <BioNudgeCard onClose={() => setBioNudge(false)} />
              : avatarNudge
                ? <AvatarNudgeCard onClose={() => setAvatarNudge(false)} />
                : backupEmailNudge && <BackupEmailNudgeCard onClose={() => setBackupEmailNudge(false)} />}
          {!nameNudge && !bioNudge && !avatarNudge && !backupEmailNudge && <AppLinksNudgeCard />}
        </React.Fragment>
      );
      case "feed_filters":
        return (
          <div key="filters" className="flex items-center gap-2 py-1 overflow-x-auto hide-scrollbar">
            {[
              ["all", t("feed.filter_all", "Tous")],
              ["preachers", t("feed.filter_preachers", "Prédicateurs")],
              ["artists", t("feed.filter_artists", "Artistes")],
              ["influencers", t("feed.filter_influencers", "Influenceurs")],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`pill shrink-0 whitespace-nowrap !text-[13px] !px-3 !py-1.5 ${filter === key ? "pill-primary" : "pill-outline"}`}
                data-testid={`filter-${key}`}
              >{label}</button>
            ))}
            {mlDiscoveryOn && showLangUi && (
            <button
              onClick={() => setMyLangOnly((v) => !v)}
              className={`pill shrink-0 whitespace-nowrap !text-[13px] !px-3 !py-1.5 ml-auto inline-flex items-center gap-1 ${myLangOnly ? "pill-primary" : "pill-outline"}`}
              data-testid="filter-my-lang"
              title={t("discover.my_lang_hint", "Afficher surtout le contenu dans ma langue")}
            ><Globe size={13} /> {t("discover.my_lang", "Ma langue")} · {myLang.toUpperCase()}</button>
            )}
          </div>
        );
      case "feed_posts":
        return <div key="posts" data-testid="feed-posts">{renderFeed()}</div>;
      default: return null;
    }
  };

  return (
    <AppShell>
      <div
        className="lg:hidden flex items-end justify-center overflow-hidden"
        style={{ height: pull, transition: pull === 0 ? "height 0.3s ease" : "none" }}
        data-testid="pull-to-refresh"
        aria-hidden={pull === 0}
      >
        <div className="flex items-center gap-2 text-xs pb-2 font-semibold" style={{ color: "var(--yalola-primary)", opacity: Math.min(1, pull / 70) }}>
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} style={{ transform: refreshing ? "none" : `rotate(${pull * 3}deg)` }} />
          {refreshing ? t("feed.refreshing", "Actualisation…") : pull >= 70 ? t("feed.release", "Relâchez pour actualiser") : t("feed.pull", "Tirez pour actualiser")}
        </div>
      </div>
      <PostListContext.Provider value={postListCtx}>
      <div className="space-y-4" data-testid="home-feed">
        <VerifyAccountReminder />
        <PendingRecoveryCard />
        <FeedModeNudgeCard scope={feedScope} />
        <ProfileCompletionCard className="mb-3" />
        {newAvailable && (
          <div className="sticky top-2 z-30 flex justify-center pointer-events-none" data-testid="feed-new-posts-wrap">
            <button
              onClick={showNewPosts}
              className="pill pill-primary shadow-lg pointer-events-auto flex items-center gap-1.5 px-4 py-2 animate-in fade-in slide-in-from-top-2"
              data-testid="feed-new-posts-btn"
            >
              <ArrowUp size={15} /> {t("feed.new_posts", "Nouvelles publications")}
            </button>
          </div>
        )}
        {/* Lot 669 — membre suivi en direct : carte LIVE (aperçu vidéo) toujours en tête du fil, hors rotation des cartes. */}
        {smart.followedLive}
        {smart.enabled ? smart.head : (
          <>
            <div className="lg:hidden"><BirthdayWidget hideWhenEmpty /></div>
            <PrayerChainBanner />
            <div className="lg:hidden"><PrayersSpotlightWidget variant="compact" /></div>
            <LiveNowWidget data={smart.live} />
            <FeaturedTestimony />
          </>
        )}
        {mainBlocks.map(renderBlock)}
      </div>
      </PostListContext.Provider>
    </AppShell>
  );
};

export default Home;
